/* TEAM VISIBILITY — the exec switch for reps seeing each other's work.
 *
 * ⭐ Brian stands in a rep's view to manage; reps stand in each other's to
 * coordinate and to compete — and whether that second half is on is his call.
 * DEFAULT OFF (`DEFAULT_ENABLED = false` on the server).
 *
 * ⚠️ THIS PANEL IS WHY `api.tamVisibility` EXISTS. Those helpers sat undeclared
 * for releases on the stated principle that "a helper with no control is not
 * progress toward a feature; it is surface area that reads as one" — and I
 * re-declared them and then shipped no control, which `check-doors` reported as
 * NO DOOR in the same build. This is the control.
 *
 * ⚠️ EXEC SEES EVERYTHING REGARDLESS. The switch was never a restriction on
 * Brian, and money stays exec-only whether it is on or off: seeing a
 * colleague's workload is not seeing their pay.
 *
 * ⚠️ ITS OWN FILE, not appended to TeamManagement.jsx — that file is 780 lines
 * and this is a self-contained read-then-write panel with its own state.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import styles from './TeamManagement.module.css'

export default function TeamVisibilityPanel({ flash }) {
  const [vis, setVis] = useState({ status: 'loading', data: null, error: null })
  const [busy, setBusy] = useState(false)
  const [reason, setReason] = useState('')

  const read = useCallback(() => {
    setVis({ status: 'loading', data: null, error: null })
    api.tamVisibility()
      .then((d) => setVis({ status: 'done', data: d, error: null }))
      .catch((err) => setVis({ status: 'error', data: null, error: String(err?.message || err) }))
  }, [])

  useEffect(() => { read() }, [read])

  const set = useCallback(async (on) => {
    setBusy(true)
    try {
      /* ⚠️ A REAL BOOLEAN — `on === true`, not `!!on`, because that is the exact
       * coercion the server refuses. `strictBool` accepts only booleans and the
       * literal strings 'true'/'false' and THROWS on anything else, since
       * `!!'false'` is `true` and a router written the obvious way would have
       * published every rep's book with `?enabled=false`. Sending a real boolean
       * means the two ends agree rather than one of them carrying the guard
       * alone. Confirmed intact 2026-10-04. */
      await api.tamVisibilitySet(on === true, reason.trim() || null)
      flash?.('ok', on
        ? 'Team visibility is ON — reps can see each other\'s work.'
        : 'Team visibility is OFF — every rep sees only their own work.')
      setReason('')
      read()
    } catch (err) {
      /* ⚠️ A FAILED WRITE SAYS THE SETTING IS UNCHANGED. The dangerous version
       * of this message is one that leaves the reader unsure whether it took. */
      flash?.('warn', `Team visibility was NOT changed: ${String(err?.message || err)}. `
        + 'The setting is unchanged.')
    } finally {
      setBusy(false)
    }
  }, [reason, read, flash])

  const d = vis.data
  const on = d?.enabled === true

  return (
    <section className={`card ${styles.panel}`}>
      <div className={styles.panelHead}>
        <div>
          <div className="eyebrow">Team visibility</div>
          <h2 className={`${styles.panelTitle} display`}>Can reps see each other&rsquo;s work?</h2>
        </div>
      </div>

      {vis.status === 'loading' && (
        <p className={styles.note}><span className="spinner" /> Reading the setting&hellip;</p>
      )}

      {/* ⚠️ A FAILURE TO READ IS NOT "OFF". Rendering it as off would state
        * something nobody knows, which is the four-false-zeros fault in a
        * setting rather than a count. */}
      {vis.status === 'error' && (
        <p className={styles.errorText} role="alert">
          <b>Could not read the setting.</b> {vis.error} — so this is NOT telling you it is off, it
          is telling you nobody knows right now.{' '}
          <button type="button" className={styles.linkBtn} onClick={read}>Try again</button>
        </p>
      )}

      {vis.status === 'done' && (
        <>
          <p className={styles.note}>
            <b>{on ? 'ON — reps can see each other.' : 'OFF — every rep sees only their own work.'}</b>
            {' '}An executive always sees everyone regardless, and money stays exec-only either way —
            seeing a colleague&rsquo;s workload is not seeing their pay.
            {d?.changedBy ? ` Last changed by ${d.changedBy}${d.at ? ` on ${d.at}` : ''}.` : ''}
          </p>

          {/* `degraded` means the store could not be READ — a different fact
            * from the setting being off, and it has to be said separately. */}
          {d?.degraded === true && (
            <p className={styles.errorText} role="alert">
              <b>The setting could not be read from its store.</b>{' '}
              {d.detail || 'It is behaving as OFF, which is the safe direction, but that is a '
                + 'failure to read rather than a decision anybody made.'}
            </p>
          )}

          <label className={styles.note}>
            <span className="eyebrow">Why (goes in the ledger)</span>
            <input
              className={styles.searchInput}
              value={reason}
              placeholder="e.g. championship season — reps coordinating on shared schools"
              onChange={(e) => setReason(e.target.value)}
            />
          </label>

          <div className={styles.rowActions}>
            <button type="button" className={styles.btn} disabled={busy || on} onClick={() => set(true)}>
              Turn ON
            </button>
            <button type="button" className={styles.btn} disabled={busy || !on} onClick={() => set(false)}>
              Turn OFF
            </button>
          </div>
        </>
      )}
    </section>
  )
}
